// Enhances the results the Bloomreach app's search block renders on /search.
// The app owns the markup and re-renders it at will, so clicks are delegated
// from the document and results are localised as the app adds them.

const STOREFRONT_API_VERSION = "2025-07";
const RESULT_SELECTOR = ".blm-product-search__result";
const COLOR_OPTION_NAMES = ["Color", "Colour"];

const config = JSON.parse(
  document.getElementById("BloomreachSearchConfig")?.textContent || "{}",
);

const PRODUCTS_QUERY = `
  query SearchResults($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
    nodes(ids: $ids) {
      ... on Product {
        id
        priceRange {
          minVariantPrice { amount currencyCode }
          maxVariantPrice { amount }
        }
        compareAtPriceRange {
          minVariantPrice { amount }
          maxVariantPrice { amount }
        }
        variants(first: 10) {
          nodes {
            selectedOptions { name value }
          }
        }
      }
    }
  }
`;

/** Products by GID, priced for the shopper's market. Keyed by GID. */
async function fetchProducts(ids) {
  const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": config.storefrontToken,
    },
    body: JSON.stringify({
      query: PRODUCTS_QUERY,
      variables: { ids, country: config.country },
    }),
  });

  if (!response.ok) throw new Error(`Storefront API: ${response.status}`);

  const { data } = await response.json();

  return new Map(
    (data?.nodes ?? []).filter(Boolean).map((node) => [node.id, node]),
  );
}

function formatPriceRange(min, max, currency) {
  const format = (amount) =>
    window.formatMoney(Number(amount), false, currency);

  return Number(max) > Number(min)
    ? `${format(min)} – ${format(max)}`
    : format(min);
}

function localizeResult(result, product) {
  const { minVariantPrice, maxVariantPrice } = product.priceRange;
  const currency = minVariantPrice.currencyCode;
  const compareMin = product.compareAtPriceRange.minVariantPrice.amount;
  const compareMax = product.compareAtPriceRange.maxVariantPrice.amount;
  const onSale =
    Number(compareMin) > Number(minVariantPrice.amount) ||
    Number(compareMax) > Number(maxVariantPrice.amount);

  const color = product.variants.nodes
    .flatMap(({ selectedOptions }) => selectedOptions)
    .find(({ name }) => COLOR_OPTION_NAMES.includes(name))?.value;

  const titleContainer = result.querySelector(
    ".blm-product-search-details-title-container",
  );

  if (color && titleContainer) {
    const colorElement = document.createElement("p");
    colorElement.className = "blm-product-search-details-title-color";
    colorElement.textContent = color;
    titleContainer.append(colorElement);
  }

  const priceContainer = result.querySelector(
    ".blm-product-search-details-container__price",
  );

  if (priceContainer) {
    const price = document.createElement("span");
    price.textContent = formatPriceRange(
      minVariantPrice.amount,
      maxVariantPrice.amount,
      currency,
    );
    priceContainer.replaceChildren(price);

    if (onSale) {
      const compareAtPrice = document.createElement("span");
      compareAtPrice.className =
        "blm-product-search-details-container__compare-price";
      compareAtPrice.textContent = formatPriceRange(
        compareMin,
        compareMax,
        currency,
      );
      priceContainer.append(compareAtPrice);
    }
  }
}

/**
 * Reprices new results in the shopper's currency and adds their colour.
 * Results are hidden until then; a product the Storefront API doesn't return
 * isn't sold in this market, so its result is removed.
 */
async function localizeResults() {
  const results = [
    ...document.querySelectorAll(`${RESULT_SELECTOR}:not([data-localized])`),
  ].filter((result) => result.dataset.productId);

  if (!results.length) return;

  results.forEach((result) => {
    result.dataset.localized = "";
    result.style.opacity = "0";
  });

  try {
    const products = await fetchProducts(
      results.map((result) => result.dataset.productId),
    );

    results.forEach((result) => {
      const product = products.get(result.dataset.productId);
      product ? localizeResult(result, product) : result.remove();
    });
  } catch (error) {
    // Bloomreach's own prices are still better than no results.
    console.error(error);
  } finally {
    results.forEach((result) => (result.style.opacity = ""));
  }
}

function markActivePrice() {
  const priceFilter = new URLSearchParams(window.location.search)
    .getAll("fq")
    .find((value) => value.startsWith("price:"));

  if (!priceFilter) return;

  const max = Number(priceFilter.split(",")[1]);
  const buttons = [...document.querySelectorAll(".price--button")];
  const active = buttons.findLast(
    (button) => Number(button.dataset.priceFilter) <= max,
  );

  buttons.forEach((button) =>
    button.classList.toggle("price--active", button === active),
  );
}

// Bloomreach's catalog links products on the store's myshopify domain, which
// only reaches the live domain through a redirect. Stopgap until the feed
// sends relative or primary-domain URLs.
function relativizeLinks() {
  const root = window.routes?.root_url ?? "/";
  const prefix = root === "/" ? "" : root;

  document
    .querySelectorAll('#br-product-search-container a[href*=".myshopify.com/"]')
    .forEach((link) => {
      const url = new URL(link.href);
      if (!url.hostname.endsWith(".myshopify.com")) return;

      link.setAttribute(
        "href",
        `${prefix}${url.pathname}${url.search}${url.hash}`,
      );
    });
}

const navigate = (url) => {
  window.location.href = url.toString();
};

function markActiveSort() {
  const sort = new URLSearchParams(window.location.search).get("sort");

  document
    .querySelectorAll(".sort--button")
    .forEach((button) =>
      button.classList.toggle(
        "sort--active",
        !!sort && button.dataset.sortValue === sort,
      ),
    );
}

const EXPANDED_CLASS = "blm-product-search-filter-button--expanded";
const filterTitle = (button) => button.textContent.trim();

// The app re-renders the filters on every change, so the open accordion group
// is remembered by its title and reopened afterwards.
let expandedFilter = null;

// Only one filter is open at a time.
function toggleFilter(button) {
  const expanded = button.classList.toggle(EXPANDED_CLASS);
  expandedFilter = expanded ? filterTitle(button) : null;

  if (!expanded) return;

  document.querySelectorAll(`.${EXPANDED_CLASS}`).forEach((other) => {
    if (other !== button) other.classList.remove(EXPANDED_CLASS);
  });
}

function restoreExpandedFilter() {
  if (!expandedFilter) return;

  const button = [
    ...document.querySelectorAll(".blm-product-search-filter-button"),
  ].find((candidate) => filterTitle(candidate) === expandedFilter);

  if (button && !button.classList.contains(EXPANDED_CLASS)) {
    button.classList.add(EXPANDED_CLASS);
  }
}

// Bloomreach re-ranks a group's values after every filter change, moving
// selected ones up. Each search keeps its values in the order they were first
// shown, set with CSS `order` so the app's markup isn't moved. Saved for the
// session, so it holds through the sort and price reloads; values not seen
// before go last.
const VALUE_ORDER_KEY = "bloomreach-search:value-order";

function readValueOrders() {
  try {
    return JSON.parse(sessionStorage.getItem(VALUE_ORDER_KEY)) ?? {};
  } catch {
    return {};
  }
}

function holdValueOrder() {
  const inputs = [
    ...document.querySelectorAll(".blm-product-search-filter-item__checkbox"),
  ];
  if (!inputs.length) return;

  const query = new URLSearchParams(window.location.search).get("q") ?? "";
  const orders = readValueOrders();
  const searchOrders = orders[query] ?? {};
  let changed = false;

  inputs.forEach(({ name, value }) => {
    const order = (searchOrders[name] ??= []);

    if (!order.includes(value)) {
      order.push(value);
      changed = true;
    }
  });

  inputs.forEach((input) => {
    const item = input.closest(".blm-product-search-filter-item");
    const position = String(searchOrders[input.name].indexOf(input.value));

    if (item && item.style.order !== position) item.style.order = position;
  });

  if (!changed) return;

  orders[query] = searchOrders;

  try {
    sessionStorage.setItem(VALUE_ORDER_KEY, JSON.stringify(orders));
  } catch {
    // The order still holds for this page.
  }
}

const DESKTOP_QUERY = window.matchMedia("(min-width: 1024px)");

// Groups that always share a desktop column, top to bottom, so the panel keeps
// the same shape as filters come and go. Matched by their Bloomreach titles:
// Price and Sort come from the app's template and are always there; the rest
// only when the results have values for them.
const FILTER_STACKS = [
  { name: "sort", titles: ["Sort", "Price"] },
  { name: "sizes", titles: ["Bag Strap Length", "Bag Size", "Jewelry Size"] },
];

// Where each stacked group came from, so unstacking puts it back in the app's
// order — the order the mobile drawer lists them in.
const stackMarkers = new WeakMap();

function plannedStacks(content) {
  const groups = new Map(
    [...content.querySelectorAll(".blm-product-search-filter")]
      .map((group) => [
        group
          .querySelector(".blm-product-search-filter-button")
          ?.textContent.trim(),
        group,
      ])
      .filter(([title]) => title),
  );

  if (!DESKTOP_QUERY.matches) return [];

  return FILTER_STACKS.map(({ name, titles }) => ({
    name,
    members: titles.map((title) => groups.get(title)).filter(Boolean),
  })).filter(({ members }) => members.length);
}

function unstack(stack) {
  [...stack.children].forEach((group) =>
    stackMarkers.get(group)?.replaceWith(group),
  );
  stack.remove();
}

// Runs after every render, which replaces the whole panel, and leaves the DOM
// alone when the stacks already match.
function layoutFilters() {
  const content = document.querySelector(".blm-product-search-sidebar-content");
  if (!content) return;

  const planned = plannedStacks(content);
  const current = [...content.querySelectorAll(".blm-filter-stack")];
  const inPlace =
    current.length === planned.length &&
    planned.every(({ name, members }) => {
      const stack = content.querySelector(`.blm-filter-stack--${name}`);

      return (
        stack?.children.length === members.length &&
        members.every((member, index) => stack.children[index] === member)
      );
    });

  if (inPlace) return;

  current.forEach(unstack);

  planned.forEach(({ name, members }) => {
    const stack = document.createElement("div");
    stack.className = `blm-filter-stack blm-filter-stack--${name}`;

    members.forEach((member) => {
      const marker = document.createComment("");
      member.before(marker);
      stackMarkers.set(member, marker);
    });

    stackMarkers.get(members[0]).before(stack);
    stack.append(...members);
  });
}

DESKTOP_QUERY.addEventListener("change", layoutFilters);

// Through the app's own toggle, so its body class and `filterpanel` URL param
// stay in step with the panel.
function closeFilters() {
  const content = document.querySelector(".blm-product-search-sidebar-content");
  if (!content?.classList.contains("blm-open")) return;

  document
    .querySelector(".blm-product-search-control-button--sidebar")
    ?.click();
}

// A second click on the active sort clears it.
function toggleSort(button) {
  const url = new URL(window.location);
  const value = button.dataset.sortValue;

  url.searchParams.get("sort") === value
    ? url.searchParams.delete("sort")
    : url.searchParams.set("sort", value);

  navigate(url);
}

// A second click on the active price clears it.
function togglePrice(button) {
  const url = new URL(window.location);
  const filters = url.searchParams
    .getAll("fq")
    .filter((value) => !value.startsWith("price:"));

  if (!button.classList.contains("price--active")) {
    filters.push(`price:0,${button.dataset.priceFilter}`);
  }

  url.searchParams.delete("fq");
  filters.forEach((value) => url.searchParams.append("fq", value));

  navigate(url);
}

document.addEventListener("click", (e) => {
  const filterButton = e.target.closest(".blm-product-search-filter-button");
  const sortButton = e.target.closest(".sort--button");
  const priceButton = e.target.closest(".price--button");

  if (filterButton) toggleFilter(filterButton);
  else if (e.target.closest(".blm-product-search-mobile-close")) closeFilters();
  else if (sortButton) toggleSort(sortButton);
  else if (priceButton) togglePrice(priceButton);
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeFilters();
});

const update = () => {
  relativizeLinks();
  markActivePrice();
  markActiveSort();
  holdValueOrder();
  layoutFilters();
  restoreExpandedFilter();
  if (config.storefrontToken) localizeResults();
};

update();
new MutationObserver(update).observe(
  document.getElementById("br-product-search-container") ?? document.body,
  { childList: true, subtree: true },
);
